/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, isDevMode, Service, signal } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import * as htmlToImage from "html-to-image";
import { map, Observable, of, Subscriber } from "rxjs";

import { AdaaHelper, printHtml } from "../../core/utils";
import { Constants } from "../constants/constants";
import {
  AdaaBoolean,
  CellType,
  FilterType,
  ImportExportStatusValues,
  ImportExportType,
  ItemStatusDs3,
  Language,
} from "../constants/enums";
import { SortEvent } from "../directives";
import {
  EntityModelType,
  GovDirection,
  IntpCountryModel,
  OrderConfig,
  OrgUnit,
  ParameterCatalog,
  RangeFilter,
  SearchFields,
  ServiceModel,
  SubGovDirection,
  TableState,
  ValueText,
} from "../models";
import { getDropdownListOptions } from "./data-table-utils-service";
import { IntpCountriesApiService } from "./intp-countries-api-service";
import { MainService } from "./main-service";
import { OrgUnitApiService } from "./org-unit-api-service";
import { PropertiesService } from "./properties-service";
import { UsersApiService } from "./users-api-service";

@Service()
export class DataTableService {
  private readonly _mainService = inject(MainService);
  private readonly _translateService = inject(TranslateService);
  private readonly _router = inject(Router);
  private readonly _propertiesService = inject(PropertiesService);
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _orgUnitApiService = inject(OrgUnitApiService);
  private readonly _intpCountriesApiService = inject(IntpCountriesApiService);

  currentSelectedEntityId = signal<number | undefined>(AdaaHelper.entity?.id);

  private _savedTablePages: {
    [key: string]: TableState;
  } = {};

  private _asyncDropdownListOptions = getDropdownListOptions();

  /**
   * Used to keep a copy of the ddl options
   * Only use this for the filters that have a relations
   */
  private _optionsDdlCopy: {
    orgUnit: OrgUnit[];
    mainService: ServiceModel[];
    auxVarService: ServiceModel[];
    govDirs: GovDirection[];
    subGovDir: SubGovDirection[];
    entities: EntityModelType[];
    countries: IntpCountryModel[];
  } = {
    orgUnit: [],
    mainService: [],
    auxVarService: [],
    govDirs: [],
    subGovDir: [],
    entities: [],
    countries: [],
  };

  readonly filtersHaveEffect: string[] = [
    "entityNameEN",
    "entityNameAE",
    "sponsorEntityNameEN",
    "sponsorEntityNameAE",
    "orgUnitNameEN",
    "orgUnitNameAE",
    "govDirectionNameEN",
    "govDirectionNameAE",
    "mainServiceNameEN",
    "mainServiceNameAE",
    "countryNameAE",
    "countryNameEN",
  ];

  public getConfig(key: string) {
    const userId = AdaaHelper.getLocalStorage(Constants.localStorageKeys.user, { type: "prop", property: "id" });
    const url = `userformconfig/getCatalogByUserIdAndSearchKey/${userId}/${key}`;

    return this._mainService.sendRequest({
      method: "GET",
      url,
    });
  }

  /**
   * @description Here we have a several conditions to check before showing any settings in the table, we might have more in the future
   * with complicated scenarios so this is why we keep it here in separate service
   * @note Make sure to take care of "actions" column since it's a static column, you might forget to handle it somewhere.
   * @param type
   * @param configListParameters
   * @param enableActionsCell
   * @param language
   */
  public getTableSettings(
    type: "displayColumns" | "originalColumns",
    configListParameters: ParameterCatalog[],
    enableActionsCell: boolean,
    language: Language
  ): string[] {
    const displayedColumns: string[] = [];
    const originalColumns: string[] = [];
    configListParameters.sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1));

    if (configListParameters) {
      configListParameters.forEach((item) => {
        if (!displayedColumns.includes(item.fieldName)) {
          originalColumns.unshift(item.fieldName);
          if (
            item.userVisibleParameter !== AdaaBoolean.N &&
            item.visibleParameter !== AdaaBoolean.N &&
            (item.step === language || !item.step) &&
            (item.lang === language || !item.lang)
          ) {
            if (AdaaHelper.isMobile() && item.mobileDisplay === AdaaBoolean.Y) {
              displayedColumns.unshift(item.fieldName);
            } else {
              displayedColumns.unshift(item.fieldName);
            }
          }
        }
      });
      if (enableActionsCell) {
        displayedColumns.push("actions");
      }
    }

    if (type === "displayColumns") {
      return displayedColumns;
    } else {
      return originalColumns;
    }
  }

  /**
   * @description Get a sorting details using searchFields then return the results as a dataTableOutput
   * @param configListParameters
   * @param sort
   */
  public getSortAndFilterData(configListParameters: ParameterCatalog[], value: any, sort?: SortEvent): SearchFields {
    const searchField: SearchFields = { searchFields: {} };

    configListParameters.forEach((column) => {
      const sortValue = sort?.column === column.fieldName ? (sort.direction === "asc" ? "asc" : "desc") : null;
      const filterValue =
        column.filterType !== FilterType.dropdownMulti &&
        column.filterType !== FilterType.dateRange &&
        column.filterType !== FilterType.valueRange &&
        column.filterType !== FilterType.YearRange
          ? value
            ? value[column.fieldName]
            : null
          : null;
      const lovValue = column.filterType === FilterType.dropdownMulti ? (value ? value[column.fieldName] : null) : null;
      const rangeValue =
        column.filterType === FilterType.dateRange ||
        column.filterType === FilterType.valueRange ||
        column.filterType === FilterType.YearRange
          ? value
            ? this._getRangeValue(
                value[column.fieldName],
                column.filterType === FilterType.dateRange || column.filterType === FilterType.YearRange
              )
            : null
          : null;

      if (
        AdaaHelper.isDefined(filterValue) ||
        AdaaHelper.isDefined(sortValue) ||
        AdaaHelper.isDefined(lovValue) ||
        AdaaHelper.isDefined(rangeValue)
      ) {
        Object.defineProperty(searchField.searchFields, column.dbFieldName, {
          value: {
            order: AdaaHelper.isDefined(sortValue) ? sortValue : null,
            value: AdaaHelper.isDefined(filterValue) ? filterValue : null,
            lov: AdaaHelper.isDefined(lovValue) ? lovValue : null,
            range: AdaaHelper.isDefined(rangeValue) ? rangeValue : null,
          },
          writable: true,
          enumerable: true,
          configurable: true,
        });
      }
    });
    return searchField;
  }

  /**
   * @description Get the sort config after the user reoreder the table columns
   * @param tableColumns
   */
  public saveColumnsConfig(tableColumns: ParameterCatalog[]) {
    const url = `userformconfig/create?isFinish=false`;
    const config = this._getColumnsConfig(tableColumns);

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data: config,
    });
  }

  public checkColumnsForFilterModal(tableColumns: ParameterCatalog[], configKey: string): ParameterCatalog[] {
    tableColumns.forEach((column) => {
      if (
        column.filterType === FilterType.dropdownMulti ||
        column.filterType === FilterType.dropdownTree ||
        column.filterType === FilterType.dropdownSingle
      ) {
        this._getDropdownListForFilter(column.fieldName, column.fieldType, column.searchKey, configKey).subscribe(
          (resp) => {
            column.dropdown = resp;
          }
        );
      }
    });

    return tableColumns;
  }

  public printTable(tableId: string, pageTitle: string) {
    const table = document.getElementById(tableId);
    const entityIcon = document.querySelector(".entity-logo") as HTMLElement;
    //Printed on white paper, so always the light logo; it is hidden in dark, so pass its URL instead of a snapshot
    const adaaLogo = document.querySelector<HTMLImageElement>(".adaa-logo.theme-light-only")?.src ?? "";

    Promise.all([htmlToImage.toSvg(table!), htmlToImage.toSvg(entityIcon), adaaLogo])
      .then((values) => {
        printHtml(
          this._buildPrintedTableHtml(this._translateService.instant(pageTitle), values[0], values[1], values[2])
        );
      })
      .catch(function (error) {
        if (isDevMode() && window.location.hostname === "localhost") {
          // eslint-disable-next-line no-console
          console.log(
            "%c Hey Developer: Please check why can't print the table" + error,
            "background: #222; color: red; font-size: 1.5em"
          );
        }
      });
  }

  public filterDataLocally(
    searchFields: SearchFields,
    tableColumns: ParameterCatalog[],
    localData: object[]
  ): object[] {
    if (!AdaaHelper.isDefined(searchFields) || !AdaaHelper.isDefined(searchFields.searchFields)) return localData;

    let results = AdaaHelper.clone(localData);
    const fields = searchFields.searchFields;
    Object.keys(fields).forEach((item) => {
      if (typeof fields[item]?.value === "string") {
        results = this._filterByValue(results, item, fields, tableColumns);
      } else if (fields[item]?.lov) {
        results = this._filterByLov(results, item, fields);
      } else if (fields[item]?.range) {
        results = this._filterByRange(results, item, fields, tableColumns);
      }
    });

    return results;
  }

  public sortDataLocally(event: SortEvent, localData: object[]): object[] {
    const results = AdaaHelper.clone(localData);

    localData = results.sort((a: any, b: any) => {
      return event.direction !== "desc"
        ? this._orderByComparator(a[event.column], b[event.column])
        : -this._orderByComparator(a[event.column], b[event.column]);
    });

    return localData;
  }

  /**
   * To save the table state
   * @param key
   * @param state
   */
  public setPageTableState(configKey: string, state: TableState): void {
    const route = this._getCurrentRouteName();
    this._savedTablePages[`${configKey}_${route}`] = state;
  }

  /**
   * To get the table state
   * @param configKey
   * @returns
   */
  public getPageTableState(configKey: string): TableState | undefined {
    const route = this._getCurrentRouteName();
    return this._savedTablePages[`${configKey}_${route}`];
  }

  /**
   * To reset all the table state
   */
  public resetAllTablesState(): void {
    this._savedTablePages = {};
    this.currentSelectedEntityId.set(AdaaHelper.entity?.id);
  }

  /**
   * Use this method to get the updated table columns with the dropdown options
   * Only use it if you have relation between two dropdowns in the data table
   * @param value
   * @param item
   * @param columns
   * @returns
   */
  public updateTableColumns(
    value: any,
    { item, configKey }: { item: ParameterCatalog; configKey: string },
    columns: ParameterCatalog[]
  ): Observable<ParameterCatalog[]> {
    if (this._isEntityAndOrgUnitLinked(configKey, item.fieldName)) {
      return configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.DATA_ACTIVITY_CONF_LIST
        ? this._handleEntityActivityChange(value, columns)
        : this._handleEntityChange(value, columns);
    }

    if (
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TRACK_LIST &&
      item.fieldName === AdaaHelper.getFieldLanguage("entityName")
    ) {
      return this._handleTrackEntityChange(value, columns);
    }

    switch (item.fieldName) {
      case AdaaHelper.getFieldLanguage("orgUnitName"):
        return this._handleOrgUnitChange(value, columns);

      case AdaaHelper.getFieldLanguage("govDirectionName"):
        return this._handleGovDirectionChange(value, columns);

      case AdaaHelper.getFieldLanguage("mainServiceName"):
        return this._handleMainServiceChange(value, columns);

      case AdaaHelper.getFieldLanguage("countryName"):
        return this._handleCountryChange(value, columns);

      default:
        return of(columns);
    }
  }

  /**
   * Handle the archived plan permission for KPIs, Objectives, Projects, Activities, ...etc.
   * @param itemId
   * @returns
   */
  public checkIfNotArchived(itemId: number): boolean {
    const isNotArchived: boolean = true;
    const planStatus =
      AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, { type: "prop", property: "status" }) ?? 0;

    if (this._checkIfItemEffectedByCycle(itemId)) {
      return planStatus !== Constants.OBJECT_STATUS.ARCHIVED && planStatus !== Constants.OBJECT_STATUS.CLOSED;
    }
    return isNotArchived;
  }

  /**
   * Get the default sort for the table if the user defined mandatory as Y from DB
   * @param configListParameters
   * @param language
   */
  public getDefaultSort(configListParameters: ParameterCatalog[], language: Language): SortEvent {
    configListParameters.sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1));

    const sortCol = configListParameters.find(
      (e) =>
        e.userVisibleParameter !== AdaaBoolean.N &&
        e.visibleParameter !== AdaaBoolean.N &&
        (e.step === language || !e.step) &&
        (e.lang === language || !e.lang) &&
        e.mandatory === AdaaBoolean.Y
    );

    return {
      column: sortCol ? sortCol.fieldName : "",
      direction: sortCol ? "asc" : "",
    };
  }

  private _getDropdownListForFilter(
    fieldName: string,
    cellType: string,
    searchKey: string,
    configKey: string
  ): Observable<ValueText[]> {
    return new Observable<ValueText[]>((subscriber) => {
      const propType = this._getPropType(fieldName, searchKey, configKey);
      if (propType) {
        this._handlePropertiesDropdown(subscriber, propType, fieldName, configKey);
      } else if (
        AdaaHelper.isDefinedAndNotEmpty(cellType) &&
        (cellType === CellType.yesNo ||
          cellType === CellType.trueFalse ||
          cellType === CellType.status ||
          cellType === CellType.agreementStatus ||
          cellType === CellType.enabled ||
          cellType === CellType.checkbox)
      ) {
        this._getDropdownListByCellType(cellType).subscribe((reps) => subscriber.next(reps));
      } else if (AdaaHelper.isDefinedAndNotEmpty(fieldName)) {
        this._getDropdownListByFieldName(fieldName, configKey).subscribe((resp) => subscriber.next(resp));
      }
    });
  }

  private _handlePropertiesDropdown(
    subscriber: Subscriber<ValueText[]>,
    propType: number | number[],
    fieldName: string,
    configKey: string
  ): void {
    const request$ = Array.isArray(propType)
      ? this._propertiesService.getPropByIdList(propType)
      : this._propertiesService.getPropById(propType);

    request$.subscribe((response) => {
      if (response.inError) return;

      let properties = response.responseData.filter((e) => e.enabled === AdaaBoolean.Y);

      properties = this._filterProperties(properties, fieldName, configKey);

      subscriber.next(
        AdaaHelper.setDropdownArray(
          properties,
          this._getDdlFilterValue(fieldName, configKey),
          AdaaHelper.getFieldLanguage("name")
        )
      );
    });
  }

  private _filterProperties(properties: any[], fieldName: string, configKey: string): any[] {
    if (fieldName === AdaaHelper.getFieldLanguage("activityType")) {
      const ids: number[] = [
        Constants.ACTIVITY_WORKFLOW_DS3.M1_MILESTONE,
        Constants.ACTIVITY_WORKFLOW_DS3.OPERATIONAL_ACTIVITY,
        Constants.ACTIVITY_WORKFLOW_DS3.STRATEGIC_ACTIVITY,
      ];

      return properties.filter((e) => ids.includes(e.id));
    }

    if (
      fieldName === AdaaHelper.getFieldLanguage("itemTypeName") &&
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.OPERATION_STRATEGIC_TASK_LIST
    ) {
      const ids: number[] = [Constants.OPERATIONS, Constants.STRATEGIC_TASKS];

      return properties.filter((e) => ids.includes(e.id));
    }

    return properties;
  }

  private _getDropdownListByCellType(cellType: string): Observable<ValueText[]> {
    switch (cellType) {
      case CellType.yesNo:
      case CellType.trueFalse:
      case CellType.checkbox:
        return this._translateService.get(["common.form.label.yes", "common.form.label.no"]).pipe(
          map((values) => {
            const isYOrN = cellType === CellType.checkbox || cellType === CellType.yesNo;

            return [
              {
                value: isYOrN ? AdaaBoolean.Y : 1,
                text: values["common.form.label.yes"],
              },
              {
                value: isYOrN ? AdaaBoolean.N : 0,
                text: values["common.form.label.no"],
              },
            ];
          })
        );
      case CellType.status:
        return this._translateService.get(["status.1", "status.3", "status.in_review"]).pipe(
          map((values) => [
            {
              value: Constants.OBJECT_STATUS.ACTIVE,
              text: values["status.1"],
            },
            {
              value: Constants.OBJECT_STATUS.DRAFT,
              text: values["status.3"],
            },
            {
              value: Constants.OBJECT_STATUS.IN_REVIEW,
              text: values["status.in_review"],
            },
          ])
        );
      case CellType.agreementStatus:
        return this._translateService.get(["published", "status.3", "status.in_review"]).pipe(
          map((values) => [
            {
              value: Constants.OBJECT_STATUS.ACTIVE,
              text: values["published"],
            },
            {
              value: Constants.OBJECT_STATUS.DRAFT,
              text: values["status.3"],
            },
            {
              value: Constants.OBJECT_STATUS.IN_REVIEW,
              text: values["status.in_review"],
            },
          ])
        );
      case CellType.enabled:
        return this._translateService.get(["common.form.label.active", "common.form.label.inactive"]).pipe(
          map((values) => [
            {
              value: AdaaBoolean.Y,
              text: values["common.form.label.active"],
            },
            {
              value: AdaaBoolean.N,
              text: values["common.form.label.inactive"],
            },
          ])
        );

      default:
        return of([]);
    }
  }

  private _getDropdownListByFieldName(fieldName: string, configKey: string): Observable<ValueText[]> {
    return new Observable<ValueText[]>((subscriber) => {
      this._handleStaticDropdowns(subscriber, fieldName, configKey);

      Object.entries(this._asyncDropdownListOptions).forEach(([key, handler]) => {
        handler(subscriber, {
          fieldName,
          configKey,
          entityId: this.currentSelectedEntityId(),
          cb: (data: any) => {
            if (key in this._optionsDdlCopy) {
              (this._optionsDdlCopy as any)[key] = data ?? [];
            }
          },
        });
      });
    });
  }

  private _handleStaticDropdowns(subscriber: Subscriber<ValueText[]>, fieldName: string, configKey: string): void {
    let config: { keys: string[]; map: (v: any) => ValueText[] } | undefined;

    if (fieldName.includes("operationType")) {
      config = {
        keys: ["workflow.approval", "workflow.review"],
        map: (values) => {
          const isApproval = fieldName === "operationType";
          return [
            {
              value: isApproval ? Constants.CONSTANT_WORKFLOW_WFTYPES.APPROVAL : values["workflow.approval"],
              text: values["workflow.approval"],
            },
            {
              value: isApproval ? Constants.CONSTANT_WORKFLOW_WFTYPES.REVIEW : values["workflow.review"],
              text: values["workflow.review"],
            },
          ];
        },
      };
    } else if (fieldName.includes("nationalProjectStatus") || fieldName.includes("annualMeetingProjectStatus")) {
      config = {
        keys: ["national_projects.completed", "national_projects.ongoing", "national_projects.delayed"],
        map: (values) => [
          {
            value: values["national_projects.completed"],
            text: values["national_projects.completed"],
          },
          {
            value: values["national_projects.ongoing"],
            text: values["national_projects.ongoing"],
          },
          {
            value: values["national_projects.delayed"],
            text: values["national_projects.delayed"],
          },
        ],
      };
    } else if (fieldName.includes("impextStatus")) {
      config = {
        keys: [
          "importExport.file_status.success",
          "importExport.file_status.done",
          "importExport.file_status.pending",
          "importExport.file_status.fail",
        ],
        map: (values) => [
          { value: ImportExportStatusValues.SUCCESS, text: values["importExport.file_status.success"] },
          { value: ImportExportStatusValues.DONE, text: values["importExport.file_status.done"] },
          { value: ImportExportStatusValues.PENDING, text: values["importExport.file_status.pending"] },
          { value: ImportExportStatusValues.FAIL, text: values["importExport.file_status.fail"] },
        ],
      };
    } else if (fieldName.includes("logType")) {
      config = {
        keys: ["dimension.log_type_values.value", "dimension.log_type_values.structure"],
        map: (values) => [
          { value: values["dimension.log_type_values.value"], text: values["dimension.log_type_values.value"] },
          {
            value: values["dimension.log_type_values.structure"],
            text: values["dimension.log_type_values.structure"],
          },
        ],
      };
    } else if (fieldName.includes("govScoreKPIType")) {
      config = {
        keys: ["government_scores.predection_kpi", "government_scores.impact_target"],
        map: (values) => [
          { value: values["government_scores.predection_kpi"], text: values["government_scores.predection_kpi"] },
          { value: values["government_scores.impact_target"], text: values["government_scores.impact_target"] },
        ],
      };
    } else if (
      fieldName.includes("type") &&
      (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.METRICS ||
        configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.DATAENTRY_METRICS)
    ) {
      config = {
        keys: ["main_types.G", "main_types.L", "main_types.D"],
        map: (values) => [
          { value: Constants.METRICS_TYPE.GLOBAL, text: values["main_types.G"] },
          { value: Constants.METRICS_TYPE.LOCAL, text: values["main_types.L"] },
          { value: Constants.METRICS_TYPE.DIMENSION, text: values["main_types.D"] },
        ],
      };
    } else if (fieldName.includes("type") && configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.IMPORT_EXPORT) {
      config = {
        keys: ["importExport.operation_type.import", "importExport.operation_type.export"],
        map: (values) => [
          { value: ImportExportType.EXPORT, text: values["importExport.operation_type.export"] },
          { value: ImportExportType.IMPORT, text: values["importExport.operation_type.import"] },
        ],
      };
    } else if (fieldName.includes("wfStatus") && configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.WORKFLOW_HISTORY) {
      config = {
        keys: ["wf_action_decision.approved", "wf_action_decision.rejected"],
        map: (values) => [
          { value: values["wf_action_decision.approved"], text: values["wf_action_decision.approved"] },
          { value: values["wf_action_decision.rejected"], text: values["wf_action_decision.rejected"] },
        ],
      };
    } else if (
      (fieldName.includes("result") || fieldName.includes("status")) &&
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.ENTITY_DATA_SYNC_LOGS
    ) {
      config = {
        keys: ["dimension.result_values.success", "dimension.result_values.started", "dimension.result_values.failed"],
        map: (values) => [
          { value: values["dimension.result_values.success"], text: values["dimension.result_values.success"] },
          { value: values["dimension.result_values.started"], text: values["dimension.result_values.started"] },
          { value: values["dimension.result_values.failed"], text: values["dimension.result_values.failed"] },
        ],
      };
    } else if (
      (fieldName.includes("status") &&
        configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.DIMENSIONS_SYNC_LOGS_VALUE_DETAILS) ||
      (fieldName.includes("action") && configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.DIMENSIONS_SYNC_LOGS_DETAILS)
    ) {
      config = {
        keys: ["dimension.action_values.create", "dimension.action_values.update"],
        map: (values) => [
          { value: values["dimension.action_values.create"], text: values["dimension.action_values.create"] },
          { value: values["dimension.action_values.update"], text: values["dimension.action_values.update"] },
        ],
      };
    } else if (
      fieldName.includes("projectStatus") &&
      (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.PROJECTS_LIST ||
        configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.M1_PROJECTS_LIST)
    ) {
      config = {
        keys: [
          "initiatives.project_status.delayed",
          "initiatives.project_status.need_improvement",
          "initiatives.project_status.on_track",
          "initiatives.project_status.completed",
          "initiatives.project_status.not_started",
        ],
        map: (values) => [
          { value: ItemStatusDs3.DELAYED, text: values["initiatives.project_status.delayed"] },
          {
            value: ItemStatusDs3.NEED_IMPROVEMENT,
            text: values["initiatives.project_status.need_improvement"],
          },
          { value: ItemStatusDs3.ON_TRACK, text: values["initiatives.project_status.on_track"] },
          {
            value: ItemStatusDs3.COMPLETED,
            text: values["initiatives.project_status.completed"],
          },
          {
            value: ItemStatusDs3.NOT_STARTED,
            text: values["initiatives.project_status.not_started"],
          },
        ],
      };
    }

    // 2. Execute translation request if config exists
    if (config) {
      this._translateService.get(config.keys).subscribe((values) => {
        subscriber.next(config.map(values));
      });
    }
  }

  private _handleEntityActivityChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const ownerColumn = columns.find((e) => e.fieldName === AdaaHelper.getFieldLanguage("ownerName"));

    const entity = this._optionsDdlCopy.entities.find((e) =>
      AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === value[0] : e.nameEN === value[0]
    );

    if (!entity) {
      // No Entity reset entity
      if (ownerColumn) ownerColumn.dropdown = [];

      return of(columns);
    }

    return new Observable<ParameterCatalog[]>((subscriber) => {
      this._usersApiService.getOwnersPerEntity(entity.id).subscribe({
        next: (response) => {
          if (response.inError) return;

          if (ownerColumn) {
            ownerColumn.dropdown = AdaaHelper.setDropdownArray(
              response.responseData,
              "id",
              AdaaHelper.getFieldLanguage("name")
            );
          }

          subscriber.next(columns);
        },
        error: (error) => {
          subscriber.error(error);
        },
      });
    });
  }

  private _handleEntityChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const orgUnitColumn = columns.find((e) => e.fieldName === AdaaHelper.getFieldLanguage("orgUnitName"));
    // ownerOrgUnitName for NTKPI table - ownerName for the reset of tables
    const ownerColumn = columns.find(
      (e) =>
        e.fieldName === AdaaHelper.getFieldLanguage("ownerName") ||
        e.fieldName === AdaaHelper.getFieldLanguage("ownerOrgUnitName")
    );

    const entity = this._optionsDdlCopy.entities.find((e) =>
      AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === value[0] : e.nameEN === value[0]
    );

    if (!entity) {
      // No Entity reset orgunit and owner to empty
      if (orgUnitColumn) orgUnitColumn.dropdown = [];
      if (ownerColumn) ownerColumn.dropdown = [];

      return of(columns);
    }

    return new Observable<ParameterCatalog[]>((subscriber) => {
      this._orgUnitApiService.getAllByEntity(entity.id).subscribe({
        next: (response) => {
          if (response.inError) return;

          if (orgUnitColumn) {
            // Update the orgUnit Copy
            this._optionsDdlCopy.orgUnit = response.responseData;

            // Entity change reset orgunit and reset the owner to empty
            orgUnitColumn.dropdown = AdaaHelper.setDropdownArray(
              response.responseData,
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("name"),
              AdaaHelper.getFieldLanguage("parentOrgUnitName")
            );
          }
          if (ownerColumn) ownerColumn.dropdown = [];

          subscriber.next(columns);
        },
      });
    });
  }

  private _handleTrackEntityChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const userColumns = columns.filter(
      (e) =>
        e.fieldName === AdaaHelper.getFieldLanguage("trackManagerName") ||
        e.fieldName === AdaaHelper.getFieldLanguage("trackCoordinatorName")
    );
    if (!userColumns.length) return of(columns);

    const entity = this._optionsDdlCopy.entities.find((e) =>
      AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === value?.[0] : e.nameEN === value?.[0]
    );

    if (!entity) {
      // No Entity reset the track users to empty
      userColumns.forEach((column) => (column.dropdown = []));
      return of(columns);
    }

    return this._usersApiService.getOwnersPerEntity(entity.id).pipe(
      map((response) => {
        const dropdown = AdaaHelper.setDropdownArray(
          response.responseData,
          AdaaHelper.getFieldLanguage("name"),
          AdaaHelper.getFieldLanguage("name")
        );
        userColumns.forEach((column) => (column.dropdown = dropdown));
        return columns;
      })
    );
  }

  private _handleOrgUnitChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const orgUnit = this._optionsDdlCopy.orgUnit.find((e) =>
      AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === value : e.nameEN === value
    );

    const ownerColumn = columns.find(
      (e) =>
        e.fieldName === AdaaHelper.getFieldLanguage("ownerName") ||
        e.fieldName === AdaaHelper.getFieldLanguage("ownerOrgUnitName")
    );

    if (!orgUnit || !ownerColumn) return of(columns);

    return new Observable<ParameterCatalog[]>((subscriber) => {
      this._usersApiService.getOwnersPerOrgUnit(orgUnit.id).subscribe({
        next: (response) => {
          ownerColumn.dropdown = AdaaHelper.setDropdownArray(
            response.responseData ?? [],
            AdaaHelper.getFieldLanguage("name"),
            AdaaHelper.getFieldLanguage("name")
          );

          subscriber.next(columns);
        },
      });
    });
  }

  private _handleGovDirectionChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const govDirNames = value ?? [];
    const govDirIds: number[] = [];
    const values: ValueText[] = [];

    for (const name of govDirNames) {
      const data = this._optionsDdlCopy.govDirs.find((e) =>
        AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === name : e.nameEN === name
      );
      if (data) govDirIds.push(data.id);
    }

    if (!govDirIds.length) {
      columns.forEach((e) => {
        if (e.fieldName === AdaaHelper.getFieldLanguage("subGovDirectionName")) {
          e.dropdown = AdaaHelper.setDropdownArray(
            this._optionsDdlCopy.subGovDir,
            AdaaHelper.getFieldLanguage("name"),
            AdaaHelper.getFieldLanguage("name")
          );
        }
      });
      return of(columns);
    }

    for (const id of govDirIds) {
      const subGov = this._optionsDdlCopy.subGovDir.filter(({ govDirectionId }) => govDirectionId === id);
      if (subGov.length) {
        values.push(
          ...AdaaHelper.setDropdownArray(
            subGov,
            AdaaHelper.getFieldLanguage("name"),
            AdaaHelper.getFieldLanguage("name")
          )
        );
      }
    }

    columns.forEach((e) => {
      if (e.fieldName === AdaaHelper.getFieldLanguage("subGovDirectionName")) {
        e.dropdown = [...values];
      }
    });

    return of(columns);
  }

  private _handleMainServiceChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const mainNames = value ?? [];
    const mainIds: number[] = [];
    const values: ValueText[] = [];

    for (const name of mainNames) {
      const data = this._optionsDdlCopy.mainService.find((e) =>
        AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === name : e.nameEN === name
      );
      if (data) mainIds.push(data.id);
    }

    if (!mainIds.length) {
      columns.forEach((e) => {
        if (e.fieldName === AdaaHelper.getFieldLanguage("auxVarServiceName")) {
          e.dropdown = AdaaHelper.setDropdownArray(
            this._optionsDdlCopy.auxVarService,
            AdaaHelper.getFieldLanguage("name"),
            AdaaHelper.getFieldLanguage("name")
          );
        }
      });
      return of(columns);
    }

    for (const id of mainIds) {
      const auxVarService = this._optionsDdlCopy.auxVarService.filter(({ parentId }) => parentId === id);
      if (auxVarService.length) {
        values.push(
          ...AdaaHelper.setDropdownArray(
            auxVarService,
            AdaaHelper.getFieldLanguage("name"),
            AdaaHelper.getFieldLanguage("name")
          )
        );
      }
    }

    columns.forEach((e) => {
      if (e.fieldName === AdaaHelper.getFieldLanguage("auxVarServiceName")) {
        e.dropdown = [...values];
      }
    });

    return of(columns);
  }

  private _handleCountryChange(value: any, columns: ParameterCatalog[]): Observable<ParameterCatalog[]> {
    const sectorColumn = columns.find((e) => e.fieldName === AdaaHelper.getFieldLanguage("sectorName"));

    if (!sectorColumn) {
      return of(columns);
    }

    const country = this._optionsDdlCopy.countries.find((e) =>
      AdaaHelper.getCurrentLang() === Language.Arabic ? e.nameAE === value[0] : e.nameEN === value[0]
    );

    if (!country) {
      sectorColumn.dropdown = [];
      return of(columns);
    }

    return new Observable<ParameterCatalog[]>((subscriber) => {
      this._intpCountriesApiService.getAllSectorsByCountry(country.id).subscribe({
        next: (response) => {
          if (response.inError) return;

          sectorColumn.dropdown = AdaaHelper.setDropdownArray(
            response.responseData,
            AdaaHelper.getFieldLanguage("name"),
            AdaaHelper.getFieldLanguage("name")
          );

          subscriber.next(columns);
        },
      });
    });
  }

  /**
   * @description The general API to find propType and call it to fetch the dropdown
   * @param field
   * @param searchKey // to know the diffrence between keys in case we have same field with 2 (dropdown list).
   */
  private _getPropType(field: string, searchKey: string, configKey: string): number | number[] | null {
    switch (field) {
      case "trend":
        return Constants.CONSTANT_TREND;

      case AdaaHelper.getFieldLanguage("valueTypeName"):
        return Constants.CONSTANT_FORMULA_LLK;

      case AdaaHelper.getFieldLanguage("measurementUnitName"):
        return Constants.CONSTANT_MEASUREMENT_UNIT;

      case AdaaHelper.getFieldLanguage("frequencyName"):
      case AdaaHelper.getFieldLanguage("frequency"):
        return Constants.CONSTANT_FREQUENCY;

      case AdaaHelper.getFieldLanguage("applicableTo"):
        return Constants.CONSTANT_KPITYPE_PROP;

      case AdaaHelper.getFieldLanguage("typeName"):
        if (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.USERS_GROUPS) return Constants.CONSTANT_SUBGOVDIRECTION;
        else if (
          configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.SERVICES ||
          configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.KHADAMATI_DETAIL
        )
          return Constants.CONSTANT_SERVICETYPES_TYPE;
        else if (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.OPERATION_STRATEGIC_TASK_LIST)
          return [Constants.CONSTANT_OPERATION_TYPES, Constants.CONSTANT_STRATEGIC_TASK_TYPES];
        else return Constants.CONSTANT_BENCHTYPE;

      case AdaaHelper.getFieldLanguage("roleName"):
        return Constants.CONSTANT_EXEC_TEAM_MEMBER_TITLE_TYPE;

      case AdaaHelper.getFieldLanguage("actionDetail"):
      case AdaaHelper.getFieldLanguage("actionType"):
      case AdaaHelper.getFieldLanguage("actionTypeName"):
      case AdaaHelper.getFieldLanguage("categoryName"):
        if (
          configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.WORKFLOW_MONITORING ||
          configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.WORKFLOW_CONFIGURATION ||
          configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.AUDIT_TRAIL
        )
          return Constants.CONSTANT_ACTIONTYPES;
        else return null;

      case AdaaHelper.getFieldLanguage("dataType"):
        if (searchKey.indexOf("metrics") > -1) return Constants.CONSTANT_MEASUREMENT_UNIT_METRICS;
        if (searchKey.indexOf("attributes") > -1) return Constants.CONSTANT_MEASUREMENT_UNIT_ATTRIBUTES;
        else return null;

      case AdaaHelper.getFieldLanguage("operationTypeName"):
        return Constants.CONSTANT_WFTYPES;

      case AdaaHelper.getFieldLanguage("itemTypeName"):
        return configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.OPERATION_STRATEGIC_TASK_LIST
          ? Constants.CONSTANT_ITEMTYPE
          : Constants.CONSTANT_ITEMTYPES;

      case AdaaHelper.getFieldLanguage("activityType"):
        return Constants.CONSTANT_ITEMTYPES;

      case AdaaHelper.getFieldLanguage("ruleSetTypeName"):
        return Constants.CONSTANT_RULESET_TYPE;

      case AdaaHelper.getFieldLanguage("scopeName"):
        return Constants.CONSTANT_RULESET_SCOPE;

      case AdaaHelper.getFieldLanguage("lastResultName"):
        return Constants.CONSTANT_RULESET_EXECUTION_RESULT;

      case AdaaHelper.getFieldLanguage("severityName"):
        return Constants.CONSTANT_RULESET_SEVERITY;

      case AdaaHelper.getFieldLanguage("priority"):
        return Constants.CONSTANT_PERIORITY;

      case AdaaHelper.getFieldLanguage("currentStatus"):
        return Constants.CONSTANT_STATUS;

      case AdaaHelper.getFieldLanguage("object"):
        return Constants.CONSTANT_ITEMS;

      case "publishedState":
        return Constants.CONSTANT_PUBLISHED_STATUS;

      case AdaaHelper.getFieldLanguage("targetCategoryName"):
        return Constants.CONSTANT_TARGET_CATEGORY;

      case AdaaHelper.getFieldLanguage("objectiveTypeName"):
        return Constants.CONSTANT_OBJECTIVE_TYPE_PROP_ID;

      case "status":
        if (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.KHADAMATI)
          return Constants.CONSTANT_KHADAMATI_IMPORT_STATUS;
        else if (configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.CYCLES) return Constants.CONSTANT_CYCLE_STATUS;
        else return null;

      case AdaaHelper.getFieldLanguage("entitySizeName"):
        return Constants.CONSTANT_ENTITY_SIZE;

      case AdaaHelper.getFieldLanguage("entityTypeName"):
        return Constants.CONSTANT_ENTITY_TYPE;

      case AdaaHelper.getFieldLanguage("attachTypeName"):
        return Constants.CONSTANT_ATTACHTYPE;

      case AdaaHelper.getFieldLanguage("mainOutcomeTypeName"):
        return Constants.CONSTANT_MAIN_OUTCOME_TYPE_PROP;

      case AdaaHelper.getFieldLanguage("action"):
        return configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.KHADAMATI_DETAIL
          ? Constants.CONSTANT_KHADAMATI_DETAILS_ACTIONS
          : null;

      case AdaaHelper.getFieldLanguage("visitType"):
        return Constants.CONSTANT_COUNTRY_VISIT_TYPE;

      case AdaaHelper.getFieldLanguage("currencyName"):
        return Constants.CONSTANT_CURRENCIES;

      case AdaaHelper.getFieldLanguage("classificationName"):
        return Constants.CONSTANT_OPERATION_STRATEGIC_TASK_CLASSIFICATIONS;

      case AdaaHelper.getFieldLanguage("taskTypeName"):
        return configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.AI_PROJECT_TASK_LIST
          ? Constants.CONSTANT_GOV_TASK_TYPES
          : null;

      default:
        return null;
    }
  }

  /**
   * @description Get the key value to the dropdown
   * @param field
   */
  private _getDdlFilterValue(field: string, configKey: string): string {
    const fieldsToCheck = [
      "frequencyName",
      "categoryName",
      "itemTypeName",
      "objectiveTypeName",
      "actionDetail",
      "objectType",
      "itemType",
      "typeName",
      "actionTypeName",
      "dataType",
      "entitySizeName",
      "entityTypeName",
      "currentStatus",
      "object",
      "priority",
      "frequency",
      "attachTypeName",
      "mainOutcomeTypeName",
      "annualMeetingProjectCycleName",
      "operationTypeName",
      "enablerEntityName",
      "action",
      "measurementUnitName",
      "targetCategoryName",
      "visitType",
      "activityType",
      "trackName",
      "classificationName",
      "taskTypeName",
    ];

    if (fieldsToCheck.some((f) => AdaaHelper.getFieldLanguage(f) === field)) {
      return AdaaHelper.getFieldLanguage("name");
    }

    if (field === "status") {
      return configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.CYCLES ? "value" : "id";
    }

    return "id";
  }

  /**
   * @description To get the start/end values if the filter is date range
   * @param value
   * @param isDateRange
   */
  private _getRangeValue(value: string, isDateRange: boolean): RangeFilter | null {
    const range: RangeFilter = {
      isDBDateTime: isDateRange ? AdaaBoolean.Y : AdaaBoolean.N,
      startNumeric: null,
      endNumeric: null,
    };

    if (!AdaaHelper.isDefinedAndNotEmpty(value)) return null;

    const valuesArr = value.split("-");
    if (!AdaaHelper.isDefined(valuesArr)) return null;

    if (AdaaHelper.isDefined(valuesArr[0]) && AdaaHelper.isDefined(valuesArr[0].replaceAll(" ", ""))) {
      range.startNumeric = parseFloat(valuesArr[0]);
    }

    if (AdaaHelper.isDefined(valuesArr[1]) && AdaaHelper.isDefined(valuesArr[1].replaceAll(" ", ""))) {
      range.endNumeric = parseFloat(valuesArr[1]);
    }

    return range;
  }

  /**
   * @description Get the sort config after the user reoreder the table columns
   * @param tableColumns
   */
  private _getColumnsConfig(tableColumns: ParameterCatalog[]): OrderConfig {
    tableColumns.forEach((e, i) => {
      e.displayOrder = i + 1;
    });

    const userId = AdaaHelper.getLocalStorage(Constants.localStorageKeys.user, {
      type: "prop",
      property: "id",
    }) as string;

    return {
      formId: tableColumns[0].formId,
      status: tableColumns[0].status,
      userId: Number(userId),
      configuration: JSON.stringify(tableColumns),
    };
  }

  /**
   * @description Get html for the printed table
   * @param object
   */
  private _buildPrintedTableHtml(
    pageTitle: string,
    tableDataUrl: string,
    entityLogo: string,
    adaaLogo: string
  ): string {
    return `<html>
        <head><title>${pageTitle}</title></head>
        <body style="width: 100%; padding: 0; margin: 0;">
        <table width="100%"><tbody>
        <tr>
        <td width="50%"><img src="${entityLogo}" /></td>
        <td width="50%" style="text-align:right"><img src="${adaaLogo}" /></td>
        </tr>
        </tbody></table>
        <h1>${pageTitle}</h1>
        <img src="${tableDataUrl}" width"100%"/>
        </body>
        </html>`;
  }

  /**
   * @description Compare two items
   * @param a
   * @param b
   */
  private _orderByComparator(a: unknown, b: unknown): number {
    const aNumber = this._toComparableNumber(a);
    const bNumber = this._toComparableNumber(b);

    if (aNumber !== undefined && bNumber !== undefined) {
      if (aNumber < bNumber) return -1;
      if (aNumber > bNumber) return 1;

      return 0;
    }

    const aText = this._toComparableString(a);
    const bText = this._toComparableString(b);

    if (aText < bText) return -1;
    if (aText > bText) return 1;

    return 0;
  }

  /**
   * @description Returns a finite number when the value can be compared numerically, otherwise undefined
   * @param value
   */
  private _toComparableNumber(value: unknown): number | undefined {
    if (typeof value === "number") return isFinite(value) ? value : undefined;

    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);

      return isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }

  /**
   * @description Coerces any value to a lowercase string so non-string columns never break the sort
   * @param value
   */
  private _toComparableString(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value.toLowerCase();

    return String(value).toLowerCase();
  }

  /**
   * @description Used for data table local filter
   * @param results
   * @param key
   * @param fields
   * @param tableColumns
   */
  private _filterByValue(results: any, key: string, fields: any, tableColumns: ParameterCatalog[]): any {
    return results.filter((e: any) => {
      if (typeof e[key] === "number") {
        if (isDevMode() && window.location.hostname === "localhost") {
          // eslint-disable-next-line no-console
          console.log(
            "%c It seems like you are comparing range value with string, make sure your input is filter by range or string, I guess your input (" +
              key +
              ") has to be part of percentageFields in data-table-constants.service.ts, if it's in a special case then please handle it as well.",
            "background: #222; color: red; font-size: 1.5em"
          );
        }
        return e[key] === fields[key]?.value;
      } else {
        const col = tableColumns.find((e) => e.dbFieldName === key);
        return col ? e[col.fieldName]?.toLowerCase().includes(fields[key]?.value?.toLowerCase()) : [];
      }
    });
  }

  /**
   * @description Used for data table local filter
   * @param results
   * @param key
   * @param fields
   */
  private _filterByLov(results: any, key: string, fields: any): any {
    const lovArray: any[] = [];

    if (fields[key]?.lov?.length > 0) {
      fields[key]?.lov?.forEach((e: any) => {
        results.forEach((data: any) => {
          if (Object.values(data)?.includes(e)) {
            lovArray.push(data);
          }
        });
      });
      results = lovArray;
    }

    return results;
  }

  /**
   * @description Used for data table local filter
   * @param results
   * @param key
   * @param fields
   */
  private _filterByRange(results: any, key: string, fields: any, tableColumns: ParameterCatalog[]): any {
    if (key.toLowerCase().includes("date")) {
      results.filter((e: any) => {
        const day = e.day ? e.day : "01";
        const month = e.month ? e.month : "01";
        const year = e.year ? e.year : new Date().getFullYear();
        if (!e[key]) {
          e[key] = new Date(`${day}-${month}-${year}`).getTime();
        }

        e[key] = AdaaHelper.getDubaiTime(e[key]);
      });
    }

    const col = tableColumns.find((e) => e.dbFieldName === key);

    const endValue = +fields[key]!.range!.endNumeric!;
    const startValue = +fields[key]!.range!.startNumeric!;

    return col
      ? (results.filter(
          (e: any) =>
            (this._checkIfNumberAndDefind(endValue) ? +e[col.fieldName] <= endValue : true) &&
            (this._checkIfNumberAndDefind(startValue) ? +e[col.fieldName] >= startValue : true)
        ) ?? [])
      : [];
  }

  /**
   * To get the current route name to be used to save the table state
   * @returns string
   */
  private _getCurrentRouteName(): string {
    let routeName = this._router.url.split("/");
    routeName = routeName.filter((c) => c !== "console" && c !== "" && c !== "dashboard");
    return routeName.length > 0 ? routeName[0] : "";
  }

  /**
   * Return true if the item not allowed to create/edit/delete in archived cycle
   * @param itemId
   * @returns
   */
  private _checkIfItemEffectedByCycle(itemId: number): boolean {
    switch (itemId) {
      case Constants.CONSTANT_PERMISSIONS.ACTIVITIES:
      case Constants.CONSTANT_PERMISSIONS.STRATEGIC_ACTIVITIES:
      case Constants.CONSTANT_PERMISSIONS.INITIATIVES:
      case Constants.CONSTANT_PERMISSIONS.STRATEGIC_PROJECT:
      case Constants.CONSTANT_PERMISSIONS.OBJECTIVES:
      case Constants.CONSTANT_PERMISSIONS.MAIN_OBJECTIVE:
      case Constants.CONSTANT_PERMISSIONS.TRANSFORMATIONAL_ObJECTIVES:
      case Constants.CONSTANT_PERMISSIONS.SERVICES_KHADAMATI:
      case Constants.CONSTANT_PERMISSIONS.DATA_ENTRY:
      case Constants.CONSTANT_PERMISSIONS.SRVKPI:
      case Constants.CONSTANT_PERMISSIONS.OPM:
      case Constants.CONSTANT_PERMISSIONS.EKPI:
      case Constants.CONSTANT_PERMISSIONS.SKPI:
      case Constants.CONSTANT_PERMISSIONS.NKPI:
      case Constants.CONSTANT_PERMISSIONS.DKPI:
      case Constants.CONSTANT_PERMISSIONS.PILLARS:
        return true;
      default:
        return false;
    }
  }

  /**
   * This sill check if the Entity And OrgUnit ddl linked or no based on the table
   * @param configKey
   * @param fieldName
   * @returns boolean
   */
  private _isEntityAndOrgUnitLinked(configKey: string, fieldName: string): boolean {
    const tableConfigs = [
      Constants.MAIN_TABLE_LIST_CONF_KEY.PMO_HOMEPAGE_PERF_AGG_CONF_LIST,
      Constants.MAIN_TABLE_LIST_CONF_KEY.AUDIT_DATA,
      Constants.MAIN_TABLE_LIST_CONF_KEY.EKPIS,
      Constants.MAIN_TABLE_LIST_CONF_KEY.NATIONAL_PROJECTS_CONF_LIST,
      Constants.MAIN_TABLE_LIST_CONF_KEY.AGM_PROJECTS_CONF_LIST,
      Constants.MAIN_TABLE_LIST_CONF_KEY.USER_ROLES_LIST,
      Constants.MAIN_TABLE_LIST_CONF_KEY.DATA_ACTIVITY_CONF_LIST,
      Constants.MAIN_TABLE_LIST_CONF_KEY.OPERATION_STRATEGIC_TASK_LIST,
    ];

    const isEntityNameLinked =
      tableConfigs.includes(configKey) && fieldName === AdaaHelper.getFieldLanguage("entityName");

    const isSponsorEntityLinked =
      configKey === Constants.MAIN_TABLE_LIST_CONF_KEY.NTKPIS &&
      fieldName === AdaaHelper.getFieldLanguage("sponsorEntityName");

    return isEntityNameLinked || isSponsorEntityLinked;
  }

  private _checkIfNumberAndDefind(value: number | undefined) {
    return AdaaHelper.isDefined(value) && !isNaN(value);
  }
}
